import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  Trash3,
  Search,
  PlusLg,
  BoxSeam,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import { useTranslation } from "react-i18next";
import {
  deleteUnits,
  getUnitsList,
  createUnits,
  updateUnits,
} from "../../services/unitsServices";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";

const Units = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    status: 0,
    short_name: "",
    min_value: "",
    uuid: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    short_name: "",
    min_value: "",
  });
  const [params, setParams] = useState({
    limit: config.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
  });
  const [unitsList, setUnitsList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [showOffAddcanvas, setShowOffAddcanvas] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value) return t("nameIsRequired");
        return "";
      case "short_name":
        if (!value) return t("shortNameIsRequired");
        return "";
      case "min_value":
        if (!value) return t("minValueIsRequired");
        if (Number(value) < 1) return t("minValueMustBeAtLeast");
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const fetchUnitsList = useCallback(() => {
    if (!isMobile) setLoading(true);

    getUnitsList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setUnitsList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        if (!isMobile) setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchUnitsList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setUnitsList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    if (loadingPage || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleDeleteUnits = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);
    deleteUnits({ uuid: deleteId.uuid })
      .then((response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);
        setUnitsList((prev) =>
          prev.filter((item) => item.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)))
      .finally(() => setBtnDeleteLoading(false));
  };

  const handleParams = (page) => setParams((prev) => ({ ...prev, page }));

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  const handleEditOpen = (e, item) => {
    setShowOffAddcanvas(true);
    setFormData({
      name: item?.name,
      status: item?.status,
      short_name: item?.short_name,
      min_value: item?.min_value,
      uuid: item?.uuid,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      short_name: validateField("short_name", formData.short_name),
      min_value: validateField("min_value", formData.min_value),
    };

    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;
    setBtnLoading(true);

    const payload = {
      name: formData.name?.trim(),
      status: formData.status,
      short_name: formData.short_name,
      min_value: formData.min_value,

      ...(formData?.uuid && { uuid: formData.uuid }),
    };

    const apiCall = formData?.uuid ? updateUnits : createUnits;

    try {
      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);

      notify.success(decrypted.message);

      // Reset form
      setShowOffAddcanvas(false);
      fetchUnitsList();
      setFormData({
        name: "",
        status: 0,
        short_name: "",
        min_value: "",
        uuid: "",
      });
      setErrors({
        name: "",
        short_name: "",
        min_value: "",
      });
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAddOpen = () => {
    setShowOffAddcanvas(true);
  };

  const handleAddClose = () => {
    setShowOffAddcanvas(false);
    setFormData({
      name: "",
      status: 0,
      short_name: "",
      min_value: "",
      uuid: "",
    });
    setErrors({
      name: "",
      short_name: "",
      min_value: "",
    });
  };

  const handleDeleteClose = () => {
    setDeleteShow(false);
  };
  const handleDeleteShow = (e, item) => {
    setDeleteId(item);
    setDeleteShow(true);
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("units")}
            </h6>
            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("unitsOnboarded")}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              className="mobile-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
            </button>

            <button
              className="desktop-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>

            <button className="add-btn" onClick={handleAddOpen}>
              <PlusLg />
              <span className="hidden md:block">{t("addNew")}</span>
            </button>
          </div>
        </div>

        <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
          <div className="overflow-auto flex-1 custom-scrollbar">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead className="bg-brand-orange sticky top-0 z-10">
                <tr>
                  <th className="table-header-text first:rounded-tl-[40px]">
                    #
                  </th>
                  <th className="table-header-text">{t("name")}</th>
                  <th className="table-header-text">{t("shortName")}</th>
                  <th className="table-header-text">{t("minValue")}</th>
                  <th className="table-header-text">{t("status")}</th>
                  <th className="table-header-text text-right last:rounded-tr-[40px]">
                    {t("action")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {loading ? (
                  <DesktopTableSkeleton
                    rows={unitsList?.length || 5}
                    columns={6}
                  />
                ) : unitsList.length > 0 ? (
                  unitsList?.map((item, i) => (
                    <tr key={item.uuid} className="border-b">
                      <td className="table-header-body font-bold">
                        {item.index}
                      </td>

                      <td className="table-header-body">{item.name}</td>
                      <td className="table-header-body">{item.short_name}</td>

                      <td className="table-header-body">{item.min_value}</td>
                      <td className="table-header-body">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            item.status === 1
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="table-header-body text-right last:rounded-tr-[40px]">
                        <div className="flex items-center justify-end gap-2">
                          <TooltipWrapper title="Edit" placement="top">
                            <button
                              className="p-1 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                              onClick={(e) => handleEditOpen(e, item)}
                            >
                              <PencilSquare />
                            </button>
                          </TooltipWrapper>
                          <TooltipWrapper title="Delete" placement="top">
                            <button
                              className="p-1 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-sm"
                              onClick={(e) => handleDeleteShow(e, item)}
                            >
                              <Trash3 />
                            </button>
                          </TooltipWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="table-no-data">
                      {t("noDataFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="md:hidden flex-1 space-y-4 overflow-y-auto pb-4 no-scrollbar"
          onScroll={handleScroll}
          style={{ marginBottom: "88px" }}
        >
          {skeletonLoading ? (
            <FlexibleSkeleton
              count={params.limit}
              showAvatar={false}
              showName
              showRole
              showContact={false}
              showButtons
            />
          ) : unitsList?.length > 0 ? (
            unitsList.map((item) => (
              <div
                key={item.uuid}
                className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[35px] p-5 shadow-sm space-y-4 mx-1 transition-colors"
              >
                <div className="w-full">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-bold text-slate-800 dark:text-white">
                      {item.name}
                    </h4>

                    <span
                      className={`text-xs px-3 py-1 rounded-md font-bold uppercase tracking-wider ${
                        item.status === 1
                          ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                      }`}
                    >
                      {item.status === 1 ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-3 text-sm text-slate-600 dark:text-slate-300">
                    <span>
                      <strong>{t("short")}:</strong> {item.short_name}
                    </span>

                    <span>
                      <strong>{t("min")}:</strong> {item.min_value}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <button
                    onClick={(e) => handleEditOpen(e, item)}
                    className="edit-mobile-btn"
                  >
                    <PencilSquare size={16} /> {t("edit")}
                  </button>
                  <button
                    onClick={(e) => handleDeleteShow(e, item)}
                    className="delete-mobile-btn"
                  >
                    <Trash3 size={16} /> {t("delete")}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">
              <BoxSeam className="no-data-icon" />
              <h3 className="no-data-title">{t("noDataFound")}</h3>
              <p className="no-data-text">{t("tryAdjustingFilter")}</p>
              <button onClick={handleReset} className="no-data-btn">
                {t("refresh")}
              </button>
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
            {t("showing")}{" "}
            <span className="font-bold text-slate-700 dark:text-slate-200">
              {startIndex + 1}
            </span>{" "}
            {t("to")}{" "}
            <span className="font-bold text-slate-700 dark:text-slate-200">
              {Math.min(endIndex, totalRecord)}
            </span>{" "}
            {t("of")} {totalRecord}
          </p>

          <div className="flex items-center gap-2">
            {/* Prev Button */}
            <button
              onClick={() => handleParams(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Desktop-only Page Numbers */}
            <div className="hidden md:flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => handleParams(page)}
                    className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                      currentPage === page
                        ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                        : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>

            {/* Mobile-only Page Indicator */}
            <div className="md:hidden px-4">
              <span className="text-sm font-black text-brand-orange">
                {t("page")} {currentPage} {t("of")} {totalPages}
              </span>
            </div>

            {/* Next Button */}
            <button
              onClick={() => handleParams(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterUnits")}
        description={t("filterYourData")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button className="btn-filter-close" onClick={handleReset}>
              {t("reset")}
            </button>

            <button className="btn-submit-orange" onClick={handleSearch}>
              {t("applyFilters")}
            </button>
          </div>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label className="input-label">{t("searchByName")}</label>
            <input
              type="text"
              className="input-field"
              placeholder={t("searchByName")}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={showOffAddcanvas}
        title={formData?.uuid ? t("updateUnits") : t("addUnits")}
        onClose={handleAddClose}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button onClick={handleAddClose} className="btn-filter-close">
              {t("close")}
            </button>

            <button
              type="button"
              form="category-form"
              className="btn-submit-orange"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? t("submitting")
                : formData.uuid
                  ? t("update")
                  : t("submit")}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="name" className="input-label">
            {t("name")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            className={`input-field ${errors.name ? "input-error" : ""}`}
            placeholder={t("enterUnitsName")}
            value={formData?.name}
            onChange={handleChange}
          />
          {errors.name && <span className="text-danger">{errors.name}</span>}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <label htmlFor="short_name" className="input-label">
            {t("shortName")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="short_name"
            className={`input-field ${errors.short_name ? "input-error" : ""}`}
            placeholder={t("enterShortsName")}
            value={formData?.short_name}
            onChange={handleChange}
          />
          {errors.short_name && (
            <span className="text-danger">{errors.short_name}</span>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <label htmlFor="min_value" className="input-label">
            {t("minValue")} <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="min_value"
            className={`input-field ${errors.min_value ? "input-error" : ""}`}
            placeholder={t("enterMinValue")}
            value={formData?.min_value}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            min={1}
          />
          {errors.min_value && (
            <span className="text-danger">{errors.min_value}</span>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <label htmlFor="status" className="input-label">
            {t("status")}
          </label>

          <div className="flex items-center gap-3 p-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="status"
                className="sr-only peer"
                checked={formData?.status === 1}
                onChange={(e) => {
                  const value = e.target.checked ? 1 : 0;
                  setFormData({ ...formData, status: value });
                }}
              />

              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-orange/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-gradient shadow-inner"></div>
            </label>
          </div>
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteUnits}
        loading={btnDeleteLoading}
        titleKey={t("deleteUnits")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={40} className="text-red-500" />}
      />
    </>
  );
};

export default Units;
